// Flat metric rail: a row of labeled stat chips with display-serif numbers
// and mono labels. Used for the PlaytestJob hero summary (W/L/D + turn-to-
// win), MetaSnapshot archetype stats, and compact dashboards.
//
// Design policy: at most ONE metric may carry tone="accent" in a single
// rail (keeps the single-accent-per-viewport rule). Numbers are tabular-
// nums display-serif; labels are mono uppercase tracked fg-subtle.

import { cn } from "../lib/cn";

export interface Metric {
  label: string;
  value: string | number;
  /** Optional small sub-value rendered in mono under the main number. */
  sub?: string;
  tone?: "default" | "accent" | "success" | "danger";
}

interface Props {
  metrics: Metric[];
  /** Size of the hero numbers. "lg" = ~42px (PlaytestJob hero),
   *  "md" = ~28px (default), "sm" = ~20px (tight contexts). */
  size?: "sm" | "md" | "lg";
  /** If true, renders as a bordered surface block with dividers.
   *  If false (default), renders flat inline — just rhythm, no chrome. */
  framed?: boolean;
  className?: string;
}

const VALUE_SIZE: Record<NonNullable<Props["size"]>, string> = {
  sm: "text-xl md:text-2xl",
  md: "text-2xl md:text-4xl",
  lg: "text-4xl md:text-5xl lg:text-[42px]",
};

const TONE: Record<NonNullable<Metric["tone"]>, string> = {
  default: "text-fg",
  accent: "text-accent",
  success: "text-success",
  danger: "text-danger",
};

export default function MetricRail({ metrics, size = "md", framed = false, className }: Props) {
  // Enforce single-accent discipline: warn in dev if >1 accent.
  const accentCount = metrics.filter((m) => m.tone === "accent").length;
  if (import.meta.env?.DEV && accentCount > 1) {
    console.warn(
      `[MetricRail] ${accentCount} metrics have tone="accent"; only one should.`,
    );
  }

  const cols = gridColsFor(metrics.length);

  return (
    <dl
      className={cn(
        "grid",
        cols,
        framed && "rounded-lg border border-border bg-bg-surface/60 overflow-hidden",
        className,
      )}
    >
      {metrics.map((m, i) => (
        <div
          key={`${m.label}-${i}`}
          className={cn(
            framed ? "p-4 md:p-5" : "py-1",
            framed && i > 0 && "border-l border-border/70",
          )}
        >
          <dt className="font-mono text-[10px] uppercase tracking-widest text-fg-subtle">
            {m.label}
          </dt>
          <dd
            className={cn(
              "mt-1.5 font-display font-medium tabular-nums leading-none",
              VALUE_SIZE[size],
              TONE[m.tone ?? "default"],
            )}
          >
            {m.value}
          </dd>
          {m.sub && (
            <div className="mt-1 font-mono text-[11px] tabular-nums text-fg-subtle">
              {m.sub}
            </div>
          )}
        </div>
      ))}
    </dl>
  );
}

function gridColsFor(n: number): string {
  if (n <= 2) return "grid-cols-2 gap-x-8 gap-y-6";
  if (n === 3) return "grid-cols-3 gap-x-6 gap-y-6";
  if (n === 4) return "grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6";
  return "grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-6";
}
